/**
 * This file defines the requestAccess() service.
 */

import type { GasPurePayload } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { AnyAri } from '@atlassian/ari/any-ari';

import type {
	AbstractServiceMarker,
	AnyServiceWithDeps,
	DependenciesContext,
} from '../../core/types';
import { toError } from '../../core/utils';
import {
	createAnalyticsEvent,
	EnablementTouchpoint,
	type GetAdditionalAttributes,
	getCurrentCloudId,
	getCurrentProduct,
	getCurrentUserId,
	getStargateURL,
	type Product,
	type UserRole,
} from '../common/common';
import { type FetchInput, type FetchResponse, fetchJSON } from '../common/fetchJSON';
import { fireErrorAnalytics } from '../common/fireErrorAnalytics';

export type RequestAccessAction = 'joined' | 'requested' | null;

export type RequestAccessResult = RequestAccessInput & {
	action: RequestAccessAction;
	resource: string;
	traceId?: string;
	userId: string;
} & (
		| { error: null; result: AccessRequestType }
		| {
				error: Error;
				result: null;
		  }
	);

export type AccessRequestType =
	| 'ACCESS_GRANTED'
	| 'PENDING_REQUEST_CREATED'
	| 'ACCESS_EXISTS'
	| 'VERIFICATION_REQUIRED'
	| 'PENDING_REQUEST_EXISTS'
	| 'DENIED_REQUEST_EXISTS'
	| 'APPROVED_REQUEST_EXISTS'
	| 'FORBIDDEN';

export type RequestAccessResponse = {
	results: {
		[resource: string]: AccessRequestType;
	};
};

// Input - data directly needed to join/raise a specific RA:
export type RequestAccessInput = {
	cloudId?: string; // If not given, defaults to dependencies.getCurrentCloudId()
	getAdditionalAttributes?: GetAdditionalAttributes<RequestAccessResult>;
	integration: string;
	note?: string;
	product: Product;
	touchpoint?: EnablementTouchpoint;
};

// Dependencies - generally common to all RA's/joins:
const dependencies: {
	createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
	fetchJSON: (<Response>(input: FetchInput) => Promise<FetchResponse<Response>>) &
		AbstractServiceMarker;
	fireErrorAnalytics: typeof fireErrorAnalytics;
	getCurrentCloudId: (() => string) & AbstractServiceMarker;
	getCurrentProduct: (() => Product) & AbstractServiceMarker;
	getCurrentUserId: (() => string) & AbstractServiceMarker;
	getStargateURL: (url: `/${string}`) => string;
} = {
	fetchJSON,
	createAnalyticsEvent,
	fireErrorAnalytics,
	getStargateURL,
	getCurrentUserId,
	getCurrentCloudId,
	getCurrentProduct,
};

type Deps = typeof dependencies;

export const requestAccess: typeof requestAccessImpl & {
	dependencies: {
		createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
		fetchJSON: (<Response>(input: FetchInput) => Promise<FetchResponse<Response>>) &
			AbstractServiceMarker;
		fireErrorAnalytics: typeof fireErrorAnalytics;
		getCurrentCloudId: (() => string) & AbstractServiceMarker;
		getCurrentProduct: (() => Product) & AbstractServiceMarker;
		getCurrentUserId: (() => string) & AbstractServiceMarker;
		getStargateURL: (url: `/${string}`) => string;
	};
} = Object.assign(requestAccessImpl, {
	dependencies,
}) satisfies AnyServiceWithDeps<Deps>;

async function requestAccessImpl(
	input: RequestAccessInput,
	context: DependenciesContext<Deps>,
): Promise<RequestAccessResult> {
	const deps = context.dependencies();
	const cloudId = input.cloudId ?? deps.getCurrentCloudId();
	const userId = deps.getCurrentUserId();
	let result: RequestAccessResult;
	const resource = AnyAri.create({
		resourceType: 'site',
		resourceId: cloudId,
		resourceOwner: input.product,
	}).toString();
	try {
		// https://developer.atlassian.com/platform/identity/rest/v1/api-group-access-requests/#api-v2-access-requests-request-post
		const fetchResponse = await deps.fetchJSON<RequestAccessResponse>({
			url: deps.getStargateURL('/invitations/v1/access-requests/request'),
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				resource,
				...(input.note ? { note: input.note } : {}),
				metadata: {
					sourceProduct: deps.getCurrentProduct(),
					...(input.touchpoint ? { touchpoint: input.touchpoint } : {}),
				},
			}),
		});
		result = mapRequestAccessResponse(fetchResponse.data, {
			resource,
			userId,
			...input,
			traceId: fetchResponse.traceId,
		});
	} catch (error) {
		result = { error: toError(error), ...input, result: null, action: null, resource, userId };
	}
	fireRequestAccessAnalytics(result, context, input.getAdditionalAttributes);
	return result;
}

function mapStatusToAction(status: AccessRequestType): RequestAccessAction {
	if (status === 'ACCESS_GRANTED') {
		return 'joined';
	}
	if (status === 'PENDING_REQUEST_CREATED') {
		return 'requested';
	}
	return null;
}

function mapRequestAccessResponse(
	response: RequestAccessResponse,
	input: RequestAccessInput & { resource: string; traceId?: string; userId: string },
): RequestAccessResult {
	const result = response.results[input.resource];
	return {
		error: null,
		result,
		action: mapStatusToAction(result),
		...input,
	};
}

// Analytics functions for these common events might later become
// exported for standalone use
function fireRequestAccessAnalytics(
	requestAccessResult: RequestAccessResult,
	context: DependenciesContext<Deps>,
	getAdditionalAttributes?: GetAdditionalAttributes<RequestAccessResult>,
) {
	const { error, product, result, traceId, integration, touchpoint, cloudId } = requestAccessResult;
	const currentUserId = context.dependencies().getCurrentUserId();
	const baseAttributes = {
		userAaid: currentUserId,
		actorAaid: currentUserId,
		roleName: 'product/member',
		sourceProduct: context.dependencies().getCurrentProduct(),
		sourceTenantId: context.dependencies().getCurrentCloudId(),
		targetProduct: product,
		targetTenantId: cloudId ?? context.dependencies().getCurrentCloudId(),
		integration,
		...(traceId ? { traceId } : {}),
	} as const;

	// touchpoint is deliberately excluded from product.joined / productAccess.requested's
	// nested `attributes`. Will consolidate when 'expansion-services-invite-capabilities' is
	// cleaned up.
	if (error) {
		if (fg('expansion-services-invite-capabilities')) {
			context.dependencies().fireErrorAnalytics({
				error,
				meta: { id: 'requestAccess' },
				attributes: {
					...(getAdditionalAttributes?.(requestAccessResult) ?? {}),
					...baseAttributes,
					...(touchpoint ? { touchpoint } : {}),
				},
			});
		}
		return;
	}

	if (fg('expansion-services-invite-capabilities')) {
		fireRequestAccessSucceeded(
			{
				...(getAdditionalAttributes?.(requestAccessResult) ?? {}),
				...baseAttributes,
				...(touchpoint ? { touchpoint } : {}),
			},
			context,
		);
	}

	const attributes = {
		...baseAttributes,
		inviteStatus: result,
	};

	if (result === 'ACCESS_GRANTED') {
		fireProductJoined(attributes, context, touchpoint);
		return;
	}

	if (!fg('expansion-services-invite-capabilities') || result === 'PENDING_REQUEST_CREATED') {
		fireProductAccessRequested(attributes, context, touchpoint);
		return;
	}
}

type AnalyticsAttributes = {
	actorAaid: string; // AAID of user initiating request (same as above if RA1/3, different if RA2)
	actorRoleName?: UserRole; // Role of the user initiating the request
	integration: string; // Integration used to identify touchpoint in our data pipelines
	inviteStatus: AccessRequestType; // USER_EXISTS, INVITED_PENDING_APPROVAL
	recommendationSources?: string[]; // Source of user invited (eg Google, Slack, Microsoft, Collab Graph?)
	roleName: UserRole; // Role being requested for the user
	sourceProduct: Product; // Product being initiated from (in case of server event with different )
	sourceTenantId: string; // TenantID of the product where the request was initiated
	targetProduct: Product; // Product being requested to
	targetTenantId: string; // TenantID of the product requested
	touchpoint?: EnablementTouchpoint; // which enablement touchpoint triggered the event
	traceId?: string; // Trace ID from the response
	userAaid: string; // AAID of user who the request is for
	userUuid?: string; // For 3P, if we don't have AAID yet: UUID of user who the request is for
	//...originAttributes TODO?
};

function fireProductAccessRequested(
	attributes: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
	touchpoint?: EnablementTouchpoint,
) {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'productAccess',
				action: 'requested',
				actionSubjectId: attributes.targetProduct,
				source: touchpoint ?? EnablementTouchpoint.UNKNOWN,
				attributes,
				tags: ['growth'],
			} satisfies GasPurePayload,
		})
		.fire();
}

function fireProductJoined(
	attributes: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
	touchpoint?: EnablementTouchpoint,
) {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'product',
				action: 'joined',
				actionSubjectId: attributes.targetProduct,
				source: touchpoint ?? EnablementTouchpoint.UNKNOWN,
				attributes,
				tags: ['growth'],
			} satisfies GasPurePayload,
		})
		.fire();
}

function fireRequestAccessSucceeded(
	attributes: Omit<AnalyticsAttributes, 'inviteStatus'>,
	context: DependenciesContext<Deps>,
) {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: 'requestAccess',
				action: 'succeeded',
				source: attributes.touchpoint ?? EnablementTouchpoint.UNKNOWN,
				attributes,
				tags: ['growth'],
			} satisfies GasPurePayload,
		})
		.fire();
}
